import { getChatCompletion } from './aiIntegrations/chatCompletion';

/**
 * Convert a File object to a base64 data URI
 */
async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });
}

/**
 * The AI service now runs as a Supabase edge function that is always
 * provisioned alongside the rest of the app — there is no per-deploy
 * endpoint toggle any more. Kept as an exported function so existing
 * upload-modal call sites (`isAiConfigured() && analyseDocument(...)`)
 * remain valid without a diff.
 */
export function isAiConfigured() {
  return true;
}

/**
 * Analyse a document file using OpenAI GPT-4o vision via Lambda
 * Supports images (JPEG, PNG) and PDFs
 *
 * @param {File} file - The uploaded file
 * @returns {Promise<{suggestions: object, confidence: object, error: string|null}>}
 */
export async function analyseDocument(file) {
  if (!isAiConfigured()) {
    return { suggestions: null, confidence: null, error: 'not_configured' };
  }

  try {
    const base64DataUri = await fileToBase64(file);
    const isImage = file?.type?.startsWith('image/');
    const isPdf = file?.type === 'application/pdf';

    if (!isImage && !isPdf) {
      return { suggestions: null, confidence: null, error: 'unsupported_type' };
    }

    const systemPrompt = `You are an expert construction compliance document analyser. 
Extract metadata from construction documents such as insurance certificates, RAMS, inspection reports, training certificates, equipment certificates, and subcontractor documents.
Always respond with valid JSON only — no markdown, no explanation.`;

    const userText = `Analyse this construction compliance document and extract the following fields. Return ONLY a JSON object with exactly these keys:
{
  "title": "document title or name",
  "category": "one of: rams, insurance, training, plant, inspection_report, subcontractor, equipment_certificate",
  "issue_date": "YYYY-MM-DD or null",
  "expiry_date": "YYYY-MM-DD or null",
  "certificate_number": "certificate or reference number or null",
  "policy_number": "insurance policy number or null",
  "contractor_name": "company or contractor name or null",
  "site_name": "site or project name or null",
  "inspection_date": "YYYY-MM-DD or null",
  "confidence": {
    "title": 0.0-1.0,
    "category": 0.0-1.0,
    "issue_date": 0.0-1.0,
    "expiry_date": 0.0-1.0,
    "certificate_number": 0.0-1.0,
    "policy_number": 0.0-1.0,
    "contractor_name": 0.0-1.0,
    "site_name": 0.0-1.0,
    "inspection_date": 0.0-1.0
  }
}
If a field cannot be determined, use null. For confidence, use 0.0 if null, higher values for clearer extractions.`;

    let messageContent;
    if (isImage) {
      messageContent = [
        { type: 'text', text: userText },
        {
          type: 'image_url',
          image_url: { url: base64DataUri, detail: 'high' }
        }
      ];
    } else {
      // PDF
      messageContent = [
        { type: 'text', text: userText },
        {
          type: 'file',
          file: { file_data: base64DataUri, filename: file?.name || 'document.pdf' }
        }
      ];
    }

    const response = await getChatCompletion(
      'OPEN_AI',
      'gpt-4o',
      [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: messageContent }
      ],
      // `max_tokens` is the Chat Completions parameter for gpt-4o.
      // `max_completion_tokens` is for the o-series reasoning models and
      // gets rejected by Chat Completions for gpt-4o/4o-mini.
      { max_tokens: 1000, temperature: 1 }
    );

    const rawContent = response?.choices?.[0]?.message?.content || '';

    // Strip markdown code fences if present
    const cleaned = rawContent?.replace(/```json\n?/g, '')?.replace(/```\n?/g, '')?.trim();
    const parsed = JSON.parse(cleaned);

    const { confidence, ...suggestions } = parsed;

    return { suggestions, confidence: confidence || {}, error: null };
  } catch (err) {
    // Propagate the real underlying message (OpenAI 400, rate-limit body,
    // JSON parse failure, etc.) so the upload modal renders something
    // actionable instead of an opaque "analysis_failed".
    console.error('AI document analysis failed:', err);
    return { suggestions: null, confidence: null, error: err?.message || 'analysis_failed' };
  }
}

/**
 * Compute document flags based on extracted data and existing documents
 *
 * @param {object} formData - Current form values
 * @param {Array} existingDocuments - Documents already in the system for this site/company
 * @returns {Array} Array of flag objects { type, label, severity }
 */
export function computeDocumentFlags(formData, existingDocuments = []) {
  const flags = [];
  const now = new Date();
  now?.setHours(0, 0, 0, 0);

  if (formData?.expiryDate) {
    const expiry = new Date(formData.expiryDate);
    expiry?.setHours(0, 0, 0, 0);
    const daysUntil = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));

    if (daysUntil < 0) {
      flags?.push({ type: 'expired', label: 'Document is expired', severity: 'error' });
    } else if (daysUntil <= 30) {
      flags?.push({
        type: 'expiring_soon',
        label: `Expiring in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}`,
        severity: 'warning'
      });
    }
  } else {
    flags?.push({ type: 'missing_expiry', label: 'No expiry date set', severity: 'warning' });
  }

  // Possible duplicate check
  if (existingDocuments?.length > 0 && (formData?.title || formData?.certificateNumber)) {
    const titleLower = formData?.title?.toLowerCase?.()?.trim();
    const certNum = formData?.certificateNumber?.trim?.();

    const duplicate = existingDocuments?.find(doc => {
      const docTitle = doc?.title?.toLowerCase?.()?.trim();
      const docCert = doc?.certificate_number?.trim?.();
      if (titleLower && docTitle && docTitle === titleLower) return true;
      if (certNum && docCert && docCert === certNum) return true;
      return false;
    });

    if (duplicate) {
      flags?.push({
        type: 'possible_duplicate',
        label: `Possible duplicate: "${duplicate?.title}" already exists`,
        severity: 'warning'
      });
    }
  }

  return flags;
}
